import React from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import useProductTour from "@/hooks/useProductTour";
import { DogIcon as Dog, TreeStructureIcon as TreeStructure, UsersFourIcon as UsersFour, PawPrintIcon as PawPrint, CalendarIcon as Calendar, HeartStraightIcon as HeartStraight, EnvelopeIcon as Envelope, ArrowsLeftRightIcon as ArrowsLeftRight, CurrencyDollarIcon as CurrencyDollar, GitForkIcon as GitFork, MagnifyingGlassIcon as MagnifyingGlass, PlayCircleIcon as PlayCircle, GraduationCapIcon as GraduationCap } from "@phosphor-icons/react";

/** Static "Getting started" guide. Linked from the Dashboard banner, the
 *  Settings page, and the AppShell drawer. Mirrors (but doesn't strictly
 *  match) the interactive product tour so users can read at their own pace.
 *  Each section deep-links to the relevant page so a new user can jump in
 *  immediately rather than just read.
 */
const STEPS = [
  {
    icon: Dog,
    title: "1. Register your dogs",
    body: "Every dog gets a registration number, photos, and a profile. Whether you have one beloved pet or a working kennel of twenty, this is where their story starts. Pedigrees are built from sire/dam links between dogs already in the registry.",
    cta: { to: "/dogs/new", label: "Register a dog" },
  },
  {
    icon: GitFork,
    title: "2. Link sires and dams",
    body: "When you create or edit a dog, the Sire and Dam pickers search every dog on the platform. As soon as both are linked the pedigree page lights up — and so does the live inbreeding-coefficient warning if there's overlap.",
    cta: { to: "/dogs", label: "Open my dogs" },
  },
  {
    icon: TreeStructure,
    title: "3. Print and share pedigrees",
    body: "Open any dog and tap Pedigree to see up to six generations as a tree. Hit Print → Save as PDF for a professional pedigree certificate, or share the public link with a buyer or family member.",
    cta: { to: "/pedigree", label: "View pedigrees" },
  },
  {
    icon: UsersFour,
    title: "4. Connect with kennels and other dog lovers",
    body: "The Breeders directory lists every kennel and serious owner on the platform. Search by breed, location, or stud availability — perfect for matchmaking your next litter or just finding people who share your breed.",
    cta: { to: "/breeders", label: "Browse breeders" },
  },
  {
    icon: PawPrint,
    title: "5. Post on The Dog's Voice",
    body: "Share photos and updates from your dog's perspective. Like, comment, and follow other breeders' dogs for a registry-wide social feed.",
    cta: { to: "/feed", label: "Open The Dog's Voice" },
  },
  {
    icon: Calendar,
    title: "6. Register for events",
    body: "Shows, fun matches, club meet-ups — find them on the Events page and pay registration fees through Stripe right inside the app.",
    cta: { to: "/events", label: "See events" },
  },
  {
    icon: HeartStraight,
    title: "7. List or find a stud",
    body: "Breeding directory: list a stud or in-season female and let the algorithm match you with compatible mates. Heat cycle reminders ping you 5–9 days ahead.",
    cta: { to: "/breeding", label: "Open breeding" },
  },
  {
    icon: ArrowsLeftRight,
    title: "8. Transfer ownership",
    body: "Selling a dog? Initiate a transfer for free. The buyer pays the $15 transfer fee via Stripe and receives the dog instantly once approved by the registry admin.",
    cta: { to: "/transfers", label: "Manage transfers" },
  },
  {
    icon: Envelope,
    title: "9. Inbox & notifications",
    body: "Transfer requests, broadcast announcements, and replies from registry admins all land in your inbox. The red badge shows your unread count.",
    cta: { to: "/inbox", label: "Check inbox" },
  },
  {
    icon: MagnifyingGlass,
    title: "10. Hit ⌘K to search anything",
    body: "Anywhere in the app, press ⌘K (Mac) / Ctrl+K (Windows) or just / to instantly jump to any dog, owner, kennel, litter, event, or post.",
    cta: null,
  },
  {
    icon: CurrencyDollar,
    title: "11. Billing & receipts",
    body: "Every payment you've made — registrations, transfer fees, event tickets — is on the Billing page with downloadable Stripe receipts.",
    cta: { to: "/billing", label: "View billing" },
  },
];

export default function Help() {
  const { replay } = useProductTour();

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto" data-testid="help-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Onboarding</p>
            <h1 className="font-spectral text-3xl md:text-4xl mt-1">Getting started</h1>
            <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
              Welcome to the registry for purebreds and exotic breeds. These
              eleven short steps cover the most useful flows — skim what's
              relevant to you, every step has a deep link so you can jump in
              immediately. Prefer a guided walk-through? Tap{" "}
              <strong className="text-ink">Replay tour</strong> below.
            </p>
          </div>
          <Button
            type="button"
            onClick={replay}
            className="rounded-full bg-ink hover:bg-crimson text-white self-start md:self-auto"
            data-testid="help-replay-tour-btn"
          >
            <PlayCircle size={16} weight="duotone" className="mr-2" />
            Replay tour
          </Button>
        </div>

        <ol className="space-y-4">
          {STEPS.map(({ icon: Icon, title, body, cta }, idx) => (
            <li
              key={title}
              className="bg-white border border-[#EAEAEA] rounded-xl p-5 md:p-6 flex gap-4 md:gap-5 items-start"
              data-testid={`help-step-${idx + 1}`}
            >
              <div className="p-3 rounded-lg bg-[#FBF7EC] border border-[#E8D9A6] flex-shrink-0">
                <Icon size={22} weight="duotone" className="text-gold" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-spectral text-xl text-ink tracking-tight">{title}</h2>
                <p className="text-sm text-[#3F3F3F] mt-2 leading-relaxed">{body}</p>
                {cta && (
                  <Link
                    to={cta.to}
                    data-testid={`help-step-${idx + 1}-cta`}
                    className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-ink hover:text-crimson transition-colors"
                  >
                    {cta.label}
                    <span aria-hidden>→</span>
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-10 bg-[#0E0E0E] text-white rounded-xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div className="flex-shrink-0 p-3 rounded-lg bg-[#C9A227]/15 border border-[#C9A227]/40">
            <GraduationCap size={28} weight="duotone" className="text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-spectral text-xl tracking-tight">Still stuck?</h3>
            <p className="text-sm text-[#D6D6D6] mt-1">
              Our admin team replies inside the registry — message us from the
              Contact link in the footer or email{" "}
              <Link to="/contact" className="underline underline-offset-2" data-testid="help-contact-link">
                support
              </Link>{" "}
              and we'll get back within one business day.
            </p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
